import React, { useEffect, useState, useMemo, useCallback, useContext } from 'react';
import { Notifications } from '../../../../../shared';
import { warehouseApi } from '../../../../../api/warehouse';
import { Typography, Button, Box, TextField } from '@material-ui/core';
import { UserContext } from '../../../../../contexts/UserContext';
import { SearchableSelect } from '../../../../../shared/components/SearchableSelect';
import { WarehouseInventoryV2Api } from '../../../../../api/warehouseInventoryV2';

const warehouseApiV2 = new WarehouseInventoryV2Api();

const AdjustCasesAndLooseUnitsAction = ({
    locationId = null,
    palletId = null,
    lotId,
    caseNum,
    looseUnitNum,
    onSuccess,
    onCancel,
    locInvType,
    palletNum,
    unitsPerCase,
    initialReason,
}) => {
    const [notification, setNotification] = useState({ text: null, level: null });
    const [showConfirmationMessage, setShowConfirmationMessage] = useState(false);
    const [confirmationMessage, setConfirmationMessage] = useState('');
    const [fromPallets, setFromPallets] = useState([]);
    const [fromPalletsLoading, setFromPalletsLoading] = useState(false);
    const [selectedFromPallet, setSelectedFromPallet] = useState();
    const { user } = useContext(UserContext);
    const [reasons, setReasons] = useState([]);
    const [reasonsLoading, setReasonsLoading] = useState(false);
    const [selectedReason, setSelectedReason] = useState(null);
    const [numCases, setNumCases] = useState('');
    const [numUnits, setNumUnits] = useState('');
    const [initialNumCases, setInitialNumCases] = useState(''); // initial values used to check for large adjustments
    const [initialNumUnits, setInitialNumUnits] = useState('');

    const showFromPalletField =
        (locInvType === 'Pallet' && palletId == null) || (locInvType === 'Both' && palletNum > 0);

    useEffect(() => {
        if (!(onSuccess instanceof Function)) {
            console.error('AdjustCasesAndLooseUnitsAction intialized incorrectly onSuccess is not a function');
        }
        if (!(onCancel instanceof Function)) {
            console.error('AdjustCasesAndLooseUnitsAction initialized incorrectly onCancel is not a function');
        }
    }, [onSuccess, onCancel]);

    const fromPalletOptions = useMemo(
        () =>
            fromPallets.map((pallet) => ({
                label: pallet.palletId.toString(),
                value: pallet.palletId,
                caseNum: pallet.numCases,
                looseUnitNum: pallet.numLooseUnits,
            })),
        [fromPallets]
    );

    const reasonOptions = useMemo(() => reasons.map((reason) => ({ label: reason, value: reason })), [reasons]);

    useEffect(() => {
        queryAdjustReasons();

        if (showFromPalletField) {
            queryFromPallets();
        } else {
            setInitialCasesAndUnits(caseNum, looseUnitNum);
        }
    }, []);

    const queryFromPallets = async () => {
        setFromPalletsLoading(true);

        const result = await warehouseApiV2.getPallets(user.facilityID, [
            { searchBy: 'locationId', searchTerm: locationId },
            { searchBy: 'lotId', searchTerm: lotId },
        ]);
        if (result?.status) {
            setFromPallets(result?.data?.pallets ?? []);
        } else {
            setNotification({ text: result.msg, level: 'error' });
        }

        setFromPalletsLoading(false);
    };

    const queryAdjustReasons = async () => {
        setReasonsLoading(true);

        const result = await warehouseApiV2.getAdjustReasons(lotId);
        if (result?.status) {
            setReasons(result?.data?.reasons ?? []);
        } else {
            setNotification({ text: result.msg, level: 'error' });
        }

        setReasonsLoading(false);
    };

    const checkForLargeAdjustment = (formNumCases, formNumUnits) => {
        const caseAdjustment = Number(formNumCases) - initialNumCases;
        const caseThreshold = 5;
        const unitThreshold = 5 * unitsPerCase;
        const unitAdjustment = Number(formNumUnits) - initialNumUnits;

        if (Math.abs(caseAdjustment) > caseThreshold || Math.abs(unitAdjustment) > unitThreshold) {
            setConfirmationMessage(
                `This is a large adjustment: Adjusting inventory by ${Math.abs(caseAdjustment)} cases and ${Math.abs(
                    unitAdjustment
                )} units.`
            );
            setShowConfirmationMessage(true);
            return;
        }

        setConfirmationMessage('');
        setShowConfirmationMessage(false);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        const requestParams = {
            lotID: lotId,
            numCases: numCases,
            numUnits: numUnits,
        };
        if (locationId) {
            requestParams.originLocationID = locationId;
        }
        if (palletId) {
            requestParams.originPalletID = palletId;
        } else if (selectedFromPallet) {
            requestParams.originPalletID = selectedFromPallet;
        }
        if (selectedReason) {
            requestParams.reason = selectedReason;
        }
        const result = await warehouseApi.adjustCaseAndUnitNums(requestParams);
        if (result.status) {
            if (!result.data.adjustmentsMade) {
                setNotification({
                    text: 'Inventory already matches the requested quantities - no adjustment made',
                    level: 'warning',
                });
            } else {
                onSuccess();
            }
        } else {
            setNotification({ text: result.msg, level: 'error' });
        }
    };

    const handleNumCasesChange = (e) => {
        setNumCases(e.target.value);
        checkForLargeAdjustment(e.target.value, numUnits);
    };

    const handleNumUnitsChange = (e) => {
        setNumUnits(e.target.value);
        checkForLargeAdjustment(numCases, e.target.value);
    };

    const setInitialCasesAndUnits = (cases, units) => {
        setInitialNumCases(cases);
        setInitialNumUnits(units);

        setNumCases(cases);
        setNumUnits(units);
    };

    const handleFromPalletChange = useCallback((newValue) => {
        setSelectedFromPallet(newValue?.value);

        setInitialCasesAndUnits(newValue?.caseNum, newValue?.looseUnitNum);
    }, []);

    const handleSelectedReasonChange = useCallback((newValue) => {
        setSelectedReason(newValue?.value);
    }, []);

    return (
        <>
            <Notifications options={notification} />
            <h3>Adjust cases and loose units</h3>

            <form onSubmit={handleSubmit}>
                <Box display="flex" flexDirection="column" style={{ gap: 10 }}>
                    {showFromPalletField && (
                        <SearchableSelect
                            onChange={handleFromPalletChange}
                            options={fromPalletOptions}
                            loading={fromPalletsLoading}
                            label="Adjust From Pallet"
                        />
                    )}
                    <SearchableSelect
                        onChange={handleSelectedReasonChange}
                        options={reasonOptions}
                        loading={reasonsLoading}
                        label="Reason"
                        initialValue={initialReason}
                    />

                    <Box display="flex" justifyContent="center" alignItems="center" style={{ gap: 20 }}>
                        <TextField
                            label="Cases"
                            name="numCases"
                            type="number"
                            value={numCases}
                            onChange={handleNumCasesChange}
                            margin="dense"
                            required
                            inputProps={{ min: 0, step: '1' }}
                        />
                        <TextField
                            label="Loose Units"
                            name="numUnits"
                            type="number"
                            value={numUnits}
                            onChange={handleNumUnitsChange}
                            margin="dense"
                            required
                            inputProps={{ min: 0, step: '1' }}
                        />
                        <Button type="submit" variant="outlined">
                            Submit
                        </Button>
                    </Box>
                </Box>
            </form>

            {showConfirmationMessage && (
                <>
                    <Typography style={{ color: 'red', marginBottom: 10 }}>{confirmationMessage}</Typography>
                </>
            )}
        </>
    );
};

export { AdjustCasesAndLooseUnitsAction };
